import { ProjectsOverviewEmptyPage } from "@components/common/empty-page/projects-overview-empty-page";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { ProjectArchivingState } from "@custom-types/project-types";
import { useFetchProjects } from "@hooks/projects/use-fetch-projects";
import { ProjectsTiles } from "@pages/projects/projects-tiles";
import { useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { useMemo } from "react";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { requiredProjectTableColumns } from "@pages/projects/projects-utils";
import { ProjectsTable } from "@components/table/projects/projects-table";
import { ProjectsMobileView } from "@pages/projects/projects-mobile-view";
import { Box } from "@mui/material";
import { Searchbar } from "@components/header/searchbar";
import { MobileActionBar } from "@components/common/mobile/mobile-action-bar";

interface Props extends BaseCompanyIdProps {
  /** The archiving-state of the projects that will be shown */
  projectArchivingState: ProjectArchivingState;

  /**
   * Flag whether the content should be shown as loading regardless whether it was loaded.
   * This is useful when the parent component know that the content should be loading,
   * e.g. it is still loading the permissions.
   */
  shouldForceLoading: boolean;
}

/**
 * The projects tab listing all the active or archived projects (depending on the props) in the company
 */
export function ProjectsOverview({
  companyId,
  projectArchivingState,
  shouldForceLoading,
}: Props): JSX.Element {
  const { projects: viewMode } = useAppSelector(dataViewModeSelector);
  const {
    projects,
    isFetchingProjects,
    hasFetchedFirstPage,
    nextPageCursor,
    sortBy,
    setSortBy,
    search,
    isDebouncingSearch,
    loadMoreProjects,
  } = useFetchProjects({
    companyId,
    projectArchivingState,
  });

  const { isScreenSmOrSmaller } = useMediaQueryList();

  /** Computes whether it is still loading to fetch all projects */
  const isLoading = useMemo(() => {
    return (
      // Set in loading state only when is fetching more projects.
      // This allows to not show the loading animation once the first page has been fetched.
      (isFetchingProjects && hasFetchedFirstPage) ||
      // For projects search only show the loading state when user is typing
      isDebouncingSearch ||
      shouldForceLoading ||
      !hasFetchedFirstPage
    );
  }, [
    isFetchingProjects,
    isDebouncingSearch,
    shouldForceLoading,
    hasFetchedFirstPage,
  ]);

  /** Flag whether the empty page should be shown, because it is no longer loading and does not have projects */
  const shouldShowEmptyPage = useMemo(() => {
    return (
      !shouldForceLoading &&
      !isFetchingProjects &&
      hasFetchedFirstPage &&
      projects.length === 0
    );
  }, [
    shouldForceLoading,
    isFetchingProjects,
    projects.length,
    hasFetchedFirstPage,
  ]);

  return (
    <>
      {/* Searchbar mounted always on mobile, but hidden on desktop to persist state on resize */}
      <Box sx={{ display: isScreenSmOrSmaller ? "block" : "none" }}>
        <MobileActionBar renderSearch={() => <Searchbar shouldFullWidth />} />
      </Box>

      {shouldShowEmptyPage && (
        <ProjectsOverviewEmptyPage
          projectArchivingState={projectArchivingState}
          isSearching={search !== undefined}
        />
      )}

      {isScreenSmOrSmaller && !shouldShowEmptyPage && (
        <ProjectsMobileView
          projects={projects}
          sortBy={sortBy}
          setSortBy={setSortBy}
          isLoading={isLoading}
          projectArchivingState={projectArchivingState}
          loadMoreProjects={loadMoreProjects}
        />
      )}
      {viewMode === "list" && !isScreenSmOrSmaller && (
        <ColumnPreferencesProvider
          tableKey="projects-table"
          columns={requiredProjectTableColumns}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: [
              "name",
              "projectManager",
              "group",
              "created",
              "lastUpdated",
              "accessLevel",
            ],
          }}
        >
          <ProjectsTable
            projects={projects}
            companyId={companyId}
            projectArchivingState={projectArchivingState}
            isLoading={isLoading}
            nextPageCursor={nextPageCursor}
            loadMoreProjects={loadMoreProjects}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </ColumnPreferencesProvider>
      )}
      {viewMode === "cards" && !isScreenSmOrSmaller && (
        <ProjectsTiles
          projects={projects}
          companyId={companyId}
          isLoading={isLoading}
          projectArchivingState={projectArchivingState}
          loadMoreProjects={loadMoreProjects}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      )}
      {/* TODO Add map: https://faro01.atlassian.net/browse/ST-3943  */}
      {/* TODO Investigate if lazy loading the map improves UX: https://faro01.atlassian.net/browse/ST-3951 */}
      {viewMode === "map" && !isScreenSmOrSmaller && <>Coming soon...</>}
    </>
  );
}
